import {Viewer} from '../main';
import {RayCaster} from "../controls";
import MeasurementController from './measurement-controller';
import {
  BufferGeometry,
  LineBasicMaterial,
  Line,
  SphereGeometry,
  MeshBasicMaterial,
  Mesh,
  Line3,
  Vector3,
  Plane
} from 'three';

export const imageOnlyDistance = 20;

export class MultipleCameraMeasurement {
  private viewer: Viewer;
  private controller: MeasurementController;
  private coordinates = [];
  private maxSeperation = 1.0;
  private maxDistance = 100.0;
  private sceneItems = [];

  constructor(controller, viewer) {
    this.viewer = viewer;
    this.controller = controller;
  }

  getEndpoint(origin, direction) {
    return direction.clone().setLength(100.0).add(origin);
  }

  drawLine(start, end, color) {
    let geometry = new BufferGeometry().setFromPoints([start, end]);
    let material = new LineBasicMaterial({color: color});
    let line = new Line(geometry, material);
    this.controller.scene.add(line);
    this.sceneItems.push(line);
  }

  drawSphere(position) {
    let geometry = new SphereGeometry( 0.05, 32, 32 );
    let material = new MeshBasicMaterial( {color: 0xffff00} );
    let sphere = new Mesh( geometry, material );
    sphere.position.copy(position);
    this.controller.scene.add(sphere);
    this.sceneItems.push(sphere);
  }

  mouseClick(event) {
    if (this.coordinates.length === 2) {
      this.sceneItems.forEach(item => {
        this.controller.scene.remove(item);
      });
      this.coordinates = [];
    }

    let raycaster = new RayCaster(this.viewer, event);
    let direction = raycaster.vectorFromEvent(event);
    let origin = this.viewer.camera.position.clone();

    // Clicked line
    let endPoint = this.getEndpoint(origin, direction);
    this.drawLine(origin, endPoint, 0xffff00);

    this.coordinates.push({
      "origin": origin,
      "direction": direction
    });

    console.log(`Image-only measure click #${this.coordinates.length}`);

    if (this.coordinates.length === 2) {
      this.finish();
    }
  }

  finish() {
    let rayA = this.coordinates[0];
    let rayB = this.coordinates[1];
    let result = this.getIntersection(rayA, rayB);
    console.log(result);
  }

  /** Get closest possible midpoint for two line segments */
  getIntersection(rayA, rayB) {
    let p1 = rayA.origin;
    let d1 = rayA.direction;
    let p2 = rayB.origin;
    let d2 = rayB.direction;

    let n = d1.clone().cross(d2);
    let n1 = d1.clone().cross(n);
    let n2 = d2.clone().cross(n);

    let t1 = p2.clone().sub(p1).dot(n2);
    let b1 = d1.clone().dot(n2);
    let v1 = d1.clone().multiplyScalar(b1 !== 0 ? t1 / b1 : 0);
    let c1 = p1.clone().add(v1);

    let t2 = p1.clone().sub(p2).dot(n1);
    let b2 = d2.clone().dot(n1);
    let v2 = d2.clone().multiplyScalar(b2 !== 0 ? t2 / b2 : 0);
    let c2 = p2.clone().add(v2);

    // Make sure our result is in front of us
    let d1n = c1.clone().sub(p1).setLength(1.0);
    let d2n = c2.clone().sub(p2).setLength(1.0);
    let inFront = (d1.angleTo(d1n) === 0) && (d2.angleTo(d2n) === 0);

    // Make sure both lines are a valid length
    let distances = [c1.distanceTo(p1), c2.distanceTo(p2)];
    let maxDistance = Math.max(...distances);
    let minDistance = Math.min(...distances);
    let validDistance = (maxDistance < this.maxDistance)
        && (minDistance > 0);

    // Get our midpoint and segment length
    let lineSegment = new Line3(c1, c2);
    let segmentLength = lineSegment.distance();
    let midPoint = new Vector3();
    lineSegment.getCenter(midPoint);
    let validSeperation = segmentLength < this.maxSeperation;

    console.log(`Intersection in front: ${inFront}`);
    console.log(`Segment Distance: [${c1.distanceTo(p1)}, ${c2.distanceTo(p2)}]`);
    console.log(`Segment Distance Valid: ${validDistance}`);
    console.log(`Seperation Distance: ${segmentLength}`);
    console.log(`Seperation Distance Valid: ${validSeperation}`);

    // Midpoint sphere
    this.drawSphere(midPoint);

    // Segment line
    this.drawLine(c1, c2, 0xff00ff);

    // All conditions must be valid
    let valid = inFront && validDistance && validSeperation;

    return {
      "point": midPoint,
      "valid": valid
    };
  }
}

export class GroundPlaneMeasurement {
  private viewer: Viewer;
  public maxDistance2D = imageOnlyDistance;

  constructor(viewer) {
    this.viewer = viewer;
  }

  getPosition(event) {
    let cameras = this.viewer.cameraList;

    let raycaster = new RayCaster(this.viewer, event);
    let direction = raycaster.vectorFromEvent(event);
    direction.setLength(this.maxDistance2D);

    let normal = new Vector3(0, 0, 1);
    let point = cameras.current.position.viewer;

    let lineStart = point.clone();
    let lineEnd = point.clone().add(direction);
    let line = new Line3(lineStart, lineEnd);

    point.z = point.z - cameras.current.height;
    let plane = new Plane().setFromNormalAndCoplanarPoint(
      normal, point);

    let intersect = new Vector3();
    let intersected = plane.intersectLine(line, intersect);
    if (!intersected) {
      return null;
    }

    return intersect;
  }
}